/**
 * BulkEditPreviewDialog — modal overlay shown when the agent proposes a bulk
 * change to the scan result.
 *
 * The agent calls either bulk_edit_scan or commit_scan_edits with confirmed=false.
 * The backend computes a diff preview and publishes this component via the
 * centerPanel channel with layout='modal'. The reviewer sees a plain-English
 * summary, a sample before/after table, and Apply / Cancel buttons.
 *
 * Apply → emits `agent_apply_bulk_edit` interaction with the source tool name;
 *          AppShell turns this into a chat message so the agent re-calls the
 *          same tool with confirmed=true.
 * Cancel → emits `agent_cancel_bulk_edit` so the modal is dismissed locally.
 *
 * Props are intentionally plain-English: no tool names or field identifiers
 * are exposed to the reviewer beyond what's already in the samples.
 */

import React from 'react';
import styles from './BulkEditPreviewDialog.module.css';

function formatValue(v) {
  if (v === null || v === undefined || v === '') return <em className={styles.blank}>(blank)</em>;
  if (typeof v === 'number') return v.toLocaleString();
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function humanizeKey(k) {
  if (!k) return k;
  return k
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function BulkEditPreviewDialog({
  summary,
  samples = [],
  transactionsExamined = 0,
  transactionsThatWouldChange = 0,
  broadChangeWarning = false,
  source = 'bulk_edit_scan',
  onInteraction,
}) {
  const handleApply = () => {
    if (onInteraction) {
      onInteraction({
        type: 'agent_apply_bulk_edit',
        source,
      });
    }
  };

  const handleCancel = () => {
    if (onInteraction) {
      onInteraction({
        type: 'agent_cancel_bulk_edit',
        source,
      });
    }
  };

  // Collect the union of changed field keys across samples to build table columns
  const changedFieldKeys = Array.from(
    samples.reduce((acc, s) => {
      Object.keys(s.before || {}).forEach((k) => acc.add(k));
      Object.keys(s.after || {}).forEach((k) => acc.add(k));
      return acc;
    }, new Set())
  );

  // A changed field gets its own before→after column; suppress the matching
  // fixed context column so the same field isn't shown twice (e.g. a
  // posting_date edit would otherwise render "Posting Date" as both a context
  // column and the change column).
  const showDescription = !changedFieldKeys.includes('description');
  const showPostingDate = !changedFieldKeys.includes('posting_date');
  const showAmount = !changedFieldKeys.includes('amount');

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog} role="dialog" aria-labelledby="bulk-edit-preview-title">
        <h3 id="bulk-edit-preview-title" className={styles.title}>
          Assistant is proposing a change
        </h3>

        <p className={styles.summary}>{summary}</p>

        <div className={styles.counts}>
          <strong>{transactionsThatWouldChange.toLocaleString()}</strong>
          {' of '}
          <strong>{transactionsExamined.toLocaleString()}</strong>
          {' transaction'}{transactionsExamined === 1 ? '' : 's'}{' will be updated.'}
        </div>

        {broadChangeWarning && (
          <div className={styles.warning}>
            ⚠ This change will update most of the transactions. Please verify this is intended before applying.
          </div>
        )}

        {samples.length > 0 && (
          <>
            <h4 className={styles.samplesHeading}>
              Sample rows (showing up to {samples.length} of {transactionsThatWouldChange.toLocaleString()})
            </h4>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {showDescription && <th>Description</th>}
                    {showPostingDate && <th>Posting Date</th>}
                    {showAmount && <th>Amount</th>}
                    {changedFieldKeys.map((k) => (
                      <th key={`h-${k}`}>{humanizeKey(k)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {samples.map((s, idx) => (
                    <tr key={idx}>
                      {showDescription && <td>{s.description || '—'}</td>}
                      {showPostingDate && <td>{s.posting_date || '—'}</td>}
                      {showAmount && <td>{s.amount != null ? formatValue(s.amount) : '—'}</td>}
                      {changedFieldKeys.map((k) => (
                        <td key={`c-${idx}-${k}`} className={styles.changeCell}>
                          <span className={styles.before}>{formatValue((s.before || {})[k])}</span>
                          <span className={styles.arrow}>→</span>
                          <span className={styles.after}>{formatValue((s.after || {})[k])}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.applyBtn}
            onClick={handleApply}
          >
            Apply to {transactionsThatWouldChange.toLocaleString()} transaction{transactionsThatWouldChange === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkEditPreviewDialog;
